import { useState, useEffect } from "react";
import axios from "axios";
import { getAuthToken, getAdminProfileData } from "../utils/cookieUtils";

/**
 * Custom hook for fetching and filtering locations based on admin role
 * @param {boolean} trigger - When to fetch locations (e.g., modal open state)
 * @param {Object} options - Additional options
 * @param {boolean} options.includeRoleFiltering - Whether to apply role-based filtering (default: true)
 * @param {number} options.perPage - Number of locations per page (default: 1000)
 * @returns {Object} { locations, loading, error, refetch }
 */
export const useLocations = (trigger = true, options = {}) => {
  const {
    includeRoleFiltering = true,
    perPage = 1000
  } = options;

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get admin profile data
  const adminProfileData = getAdminProfileData();
  const currentAdminRoleId = adminProfileData?.message?.admin?.role_id;
  const currentAdminLocationId = adminProfileData?.message?.admin?.location_id;

  const fetchLocations = async () => {
    if (!trigger) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = getAuthToken();
      const response = await axios.get("/super-admin/locations", {
        headers: { Authorization: `Bearer ${token}` },
        params: { per_page: perPage },
      });

      let allLocations = response.data?.data?.data || [];

      // Filter out inactive locations first - only show active locations in dropdowns
      allLocations = allLocations.filter(loc => loc.status === 'active');

      // Apply role-based filtering if enabled
      if (includeRoleFiltering) {
        // Branch Admin (role_id = 4) and Admin (role_id = 2) should only see their own location
        if ((currentAdminRoleId === 4 || currentAdminRoleId === 2) && currentAdminLocationId) {
          allLocations = allLocations.filter(loc => loc.id === currentAdminLocationId);
        }
        // Super Admin (role_id = 3) can see all active locations
      }

      setLocations(allLocations);
    } catch (err) {
      console.error("Error fetching locations:", err);
      setError("Failed to fetch locations");
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, [trigger, currentAdminRoleId, currentAdminLocationId, includeRoleFiltering, perPage]);

  // Provide a refetch function for manual refresh
  const refetch = () => {
    fetchLocations();
  };

  return {
    locations,
    loading,
    error,
    refetch,
    // Additional utility values
    currentAdminRoleId,
    currentAdminLocationId,
    isBranchAdmin: currentAdminRoleId === 4,
    isSuperAdmin: currentAdminRoleId === 3,
    isRegularAdmin: currentAdminRoleId === 2
  };
};

export default useLocations;